import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { isRealSite, stripRealPrefix } from "./utils/siteMode";
import { AuthProvider } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import Login from "./components/Login";
import Home from "./components/Home";
import Profile from "./components/Profile";
import Products from "./components/Products";
import ProductDetail from "./components/ProductDetail";
import ManualViewerPage from "./components/ManualViewerPage";
import StudioEmbed from "./components/StudioEmbed";
import StudioVideos from "./components/StudioVideos";
import Forum from "./components/Forum";
import Tutorials from "./components/Tutorials";
import "./App.css";

/** 兼容旧书签 /real/* → 主站路径 */
function RealLegacyRedirect() {
  const location = useLocation();
  const to = `${stripRealPrefix(location.pathname)}${location.search}${location.hash}`;
  return <Navigate to={to || "/"} replace />;
}

const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

function About() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const m = (base, mobile) => (isMobile ? mobile : base);
  const showCommunities = isRealSite(location.pathname);

  // 店铺信息配置
  const shops = [
    {
      name: "B站",
      url: " https://b23.tv/c9gCtts", // 请替换为实际店铺链接
      qrCode: `${process.env.PUBLIC_URL}/qrcode-bilibili.png`,
      icon: "📺",
    },
    {
      name: "淘宝",
      url: "https://shop435871840.taobao.com/?spm=a21n57.shop_search.0.0.157856f4X9PcCD", // 请替换为实际店铺链接
      qrCode: `${process.env.PUBLIC_URL}/qrcode-taobao.png`, // 请将二维码图片放在 public 目录
      icon: "🛒",
    },
    {
      name: "小红书",
      url: "https://xhslink.com/m/99bvw2AECiM", // 请替换为实际店铺链接
      qrCode: `${process.env.PUBLIC_URL}/qrcode-xiaohongshu.png`,
      icon: "📕",
    },
    {
      name: "闲鱼",
      url: "https://www.goofish.com/personal?spm=a21ybx.item.itemHeader.1.21fe3da6YhPpLc&userId=762873677", // 请替换为实际店铺链接
      qrCode: `${process.env.PUBLIC_URL}/qrcode-xianyu.png`,
      icon: "🐟",
    },
    {
      name: "抖音",
      url: "https://www.douyin.com/user/MS4wLjABAAAA6PC2fqKBgph03Twih_7Xa8tZ2jTKQ0lHrXXbjaR2enw?from_tab_name=main", // 请替换为实际店铺链接
      qrCode: `${process.env.PUBLIC_URL}/qrcode-douyin.png`,
      icon: "🎵",
    },
  ];

  // 用户社群配置
  const communities = [
    {
      name: "QQ群",
      url: "https://qun.qq.com/universal-share/share?ac=1&authKey=t9WVXgVZFvWvTHVWyzpghwG7FxJ8%2B3i3aJybWb7Bfl1tBNcH7ZoqawAOdxkVULvf&busi_data=eyJncm91cENvZGUiOiIxMDE3Nzc3NTEzIiwidG9rZW4iOiJOVUNmYWF3aWV0NzE0VEsvZklaQjA4YzhvazkzZC9JN21lNmFpdnFuYTg3d0J1Z0Q3OHljcVhpeFJuNmgxYldZIiwidWluIjoiODE0NTAwMTg2In0%3D&data=kL7v9y6YTbm5YzxxiNfQQ5RFJXyP3e1EaiWv35_dFT1yeUn_I9x5hMnJ4Us8NT85H3s_s6g0Tf06NAuvc701FA&svctype=4&tempid=h5_group_info",
      qrCode: `${process.env.PUBLIC_URL}/qrcode-qq.png`,
      titleImage: `${process.env.PUBLIC_URL}/comtitle-qq.png`,
      icon: "💬",
    },
    {
      name: "小红书群",
      url: "", // 请替换为实际链接
      qrCode: `${process.env.PUBLIC_URL}/qrcode-xiaohongshu-group.png`,
      titleImage: `${process.env.PUBLIC_URL}/comtitle-xiaohongshu.png`,
      icon: "📕",
    },
    {
      name: "微信群",
      url: "", // 请替换为实际链接
      qrCode: `${process.env.PUBLIC_URL}/qrcode-wechat-group.png`,
      titleImage: `${process.env.PUBLIC_URL}/comtitle-wechat.png`,
      icon: "💚",
    },
    {
      name: "Discord",
      url: "https://discord.gg/cuNVGmXF",
      qrCode: `${process.env.PUBLIC_URL}/qrcode-discord.png`,
      titleImage: `${process.env.PUBLIC_URL}/comtitle-discord.png`,
      icon: "🎮",
    },
  ];

  return (
    <div style={m(styles.page, { ...styles.page, padding: "24px 16px" })}>
      <div style={styles.container}>
        <h1 style={m(styles.title, { ...styles.title, fontSize: 24, marginBottom: 20 })}>
          {showCommunities ? '关于蓝卡模玩社区' : '关于蓝卡模玩工作室'}
        </h1>

        {/* 工作室简介 */}
        <section style={m(styles.section, { ...styles.section, marginBottom: 28 })}>
          <h2 style={m(styles.sectionTitle, { ...styles.sectionTitle, fontSize: 18, marginBottom: 12, paddingBottom: 8 })}>工作室简介</h2>
          <div style={m(styles.introBox, { ...styles.introBox, padding: 16, marginBottom: 12 })}>
            <p style={m(styles.introText, { ...styles.introText, fontSize: 14, lineHeight: 1.6, marginBottom: 10 })}>
              蓝卡模玩工作室是一个专注于模玩创作与分享的专业团队，致力于为模玩爱好者提供最优质的创作体验和产品服务。
            </p>
            <p style={m(styles.introText, { ...styles.introText, fontSize: 14, lineHeight: 1.6, marginBottom: 10 })}>
              我们拥有丰富的3D打印经验，擅长各类模玩的设计、制作与定制。无论是原创设计还是个性化定制，我们都能为您提供专业的技术支持和优质的服务体验。
            </p>
            <p style={m(styles.introText, { ...styles.introText, fontSize: 14, lineHeight: 1.6, marginBottom: 0 })}>
              欢迎关注我们的各个平台，获取最新作品、教程分享和优惠信息！
            </p>
          </div>
        </section>

        {/* 用户社群 */}
        {showCommunities && (
        <section style={m(styles.section, { ...styles.section, marginBottom: 28 })}>
          <h2 style={m(styles.sectionTitle, { ...styles.sectionTitle, fontSize: 18, marginBottom: 12, paddingBottom: 8 })}>用户社群</h2>
          <div style={m(styles.communitiesGrid, { ...styles.communitiesGrid, gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 12 })}>
            {communities.map((community, index) =>
              community.url ? (
                <a
                  key={index}
                  href={community.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={m(styles.communityCard, { ...styles.communityCard, padding: 12, borderRadius: 12 })}
                  className="shop-card-link"
                >
                  <div style={m(styles.communityIcon, { ...styles.communityIcon, fontSize: 28, marginBottom: 6 })}>{community.icon}</div>
                  <h3 style={m(styles.communityName, { ...styles.communityName, fontSize: 14, marginBottom: 8 })}>{community.name}</h3>

                  <div style={m(styles.qrCodeContainer, { ...styles.qrCodeContainer, width: 90, height: 90, marginBottom: 8 })}>
                    <img
                      src={community.qrCode}
                      alt={`${community.name}二维码`}
                      style={styles.qrCode}
                      onError={(e) => {
                        // 如果二维码图片不存在，显示占位符
                        e.target.style.display = "none";
                        const placeholder =
                          e.target.parentElement.querySelector(
                            ".qr-placeholder"
                          );
                        if (placeholder) {
                          placeholder.style.display = "flex";
                        }
                      }}
                    />

                    <div
                      className="qr-placeholder"
                      style={styles.qrCodePlaceholder}
                    >
                      <div style={styles.placeholderIcon}>📱</div>
                      <div style={styles.placeholderText}>二维码</div>
                    </div>
                  </div>

                  {community.titleImage && (
                    <img
                      src={community.titleImage}
                      alt={`${community.name}标题`}
                      style={m(styles.communityTitleImage, { ...styles.communityTitleImage, maxWidth: 100, marginBottom: 8 })}
                    />
                  )}

                  <div style={m(styles.shopLink, { ...styles.shopLink, fontSize: 12 })}>点击加入 →</div>
                </a>
              ) : (
                <div key={index} style={m(styles.communityCard, { ...styles.communityCard, padding: 12, borderRadius: 12 })}>
                  <div style={m(styles.communityIcon, { ...styles.communityIcon, fontSize: 28, marginBottom: 6 })}>{community.icon}</div>
                  <h3 style={m(styles.communityName, { ...styles.communityName, fontSize: 14, marginBottom: 8 })}>{community.name}</h3>
                  <div style={m(styles.qrCodeContainer, { ...styles.qrCodeContainer, width: 90, height: 90, marginBottom: 8 })}>
                    <img
                      src={community.qrCode}
                      alt={`${community.name}二维码`}
                      style={styles.qrCode}
                      onError={(e) => {
                        // 如果二维码图片不存在，显示占位符
                        e.target.style.display = "none";
                        const placeholder =
                          e.target.parentElement.querySelector(
                            ".qr-placeholder"
                          );
                        if (placeholder) {
                          placeholder.style.display = "flex";
                        }
                      }}
                    />
                    <div
                      className="qr-placeholder"
                      style={styles.qrCodePlaceholder}
                    >
                      <div style={styles.placeholderIcon}>📱</div>
                      <div style={styles.placeholderText}>二维码</div>
                    </div>
                  </div>

                  {community.titleImage && (
                    <img
                      src={community.titleImage}
                      alt={`${community.name}标题`}
                      style={m(styles.communityTitleImage, { ...styles.communityTitleImage, maxWidth: 100, marginBottom: 8 })}
                    />
                  )}
                </div>
              )
            )}
          </div>
        </section>
        )}

        {/* 店铺矩阵 */}
        <section style={m(styles.section, { ...styles.section, marginBottom: 28 })}>
          <h2 style={m(styles.sectionTitle, { ...styles.sectionTitle, fontSize: 18, marginBottom: 12, paddingBottom: 8 })}>店铺矩阵</h2>
          <div style={m(styles.shopsGrid, { ...styles.shopsGrid, gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 12 })}>
            {shops.map((shop, index) => (
              <a
                key={index}
                href={shop.url}
                target="_blank"
                rel="noopener noreferrer"
                style={m(styles.shopCard, { ...styles.shopCard, padding: 12, borderRadius: 12 })}
                className="shop-card-link"
              >
                <div style={m(styles.shopIcon, { ...styles.shopIcon, fontSize: 28, marginBottom: 6 })}>{shop.icon}</div>
                <h3 style={m(styles.shopName, { ...styles.shopName, fontSize: 14, marginBottom: 8 })}>{shop.name}</h3>
                <div style={m(styles.qrCodeContainer, { ...styles.qrCodeContainer, width: 90, height: 90, marginBottom: 8 })}>
                  <img
                    src={shop.qrCode}
                    alt={`${shop.name}二维码`}
                    style={styles.qrCode}
                    onError={(e) => {
                      // 如果二维码图片不存在，显示占位符
                      e.target.style.display = "none";
                      const placeholder =
                        e.target.parentElement.querySelector(".qr-placeholder");
                      if (placeholder) {
                        placeholder.style.display = "flex";
                      }
                    }}
                  />
                  <div
                    className="qr-placeholder"
                    style={styles.qrCodePlaceholder}
                  >
                    <div style={styles.placeholderIcon}>📱</div>
                    <div style={styles.placeholderText}>二维码</div>
                  </div>
                </div>
                <div style={m(styles.shopLink, { ...styles.shopLink, fontSize: 12 })}>点击访问 →</div>
              </a>
            ))}
          </div>
        </section>

        {/* 联系我们 */}
        <section style={m(styles.section, { ...styles.section, marginBottom: 24 })}>
          <h2 style={m(styles.sectionTitle, { ...styles.sectionTitle, fontSize: 18, marginBottom: 12, paddingBottom: 8 })}>联系我们</h2>
          <div style={m(styles.contactBox, { ...styles.contactBox, padding: 20, marginTop: 12, gap: 16 })}>
            <div style={m(styles.contactItem, { ...styles.contactItem, gap: 12 })}>
              <div style={m(styles.contactIcon, { ...styles.contactIcon, fontSize: 24 })}>📧</div>
              <div style={styles.contactInfo}>
                <div style={m(styles.contactLabel, { ...styles.contactLabel, fontSize: 12 })}>邮箱</div>
                <a
                  href="mailto:lankafunfun@gmail.com"
                  style={m(styles.contactValue, { ...styles.contactValue, fontSize: 14 })}
                >
                  lankafunfun@gmail.com
                </a>
              </div>
            </div>
            <div style={m(styles.contactItem, { ...styles.contactItem, gap: 12 })}>
              <div style={m(styles.contactIcon, { ...styles.contactIcon, fontSize: 24 })}>📞</div>
              <div style={styles.contactInfo}>
                <div style={m(styles.contactLabel, { ...styles.contactLabel, fontSize: 12 })}>电话</div>
                <a href="tel:+8617783079113" style={m(styles.contactValue, { ...styles.contactValue, fontSize: 14 })}>
                  +86 17783079113
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Settings() {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.title}>设置</h1>
        <p style={styles.text}>设置功能正在开发中...</p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "90vh",
    background: "linear-gradient(180deg, #1a1d23 0%, #282c34 100%)",
    padding: "60px 20px",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    textAlign: "center",
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#ffffff",
    marginBottom: "40px",
  },
  text: {
    fontSize: "16px",
    color: "#9ca3af",
    lineHeight: "1.8",
  },
  section: {
    marginBottom: "60px",
    textAlign: "left",
  },
  sectionTitle: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#ffffff",
    marginBottom: "24px",
    textAlign: "center",
    borderBottom: "2px solid #3b82f6",
    paddingBottom: "12px",
    display: "inline-block",
    width: "100%",
  },
  introBox: {
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: "12px",
    padding: "30px",
    marginBottom: "20px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  },
  introText: {
    fontSize: "16px",
    color: "#d1d5db",
    lineHeight: "1.8",
    marginBottom: "16px",
    textAlign: "left",
  },
  shopsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "24px",
    marginTop: "30px",
  },
  shopCard: {
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: "16px",
    padding: "24px",
    textDecoration: "none",
    color: "inherit",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    transition: "all 0.3s ease",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
  },
  shopIcon: {
    fontSize: "48px",
    marginBottom: "12px",
  },
  shopName: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#ffffff",
    marginBottom: "16px",
  },
  qrCodeContainer: {
    width: "140px",
    height: "140px",
    marginBottom: "16px",
    position: "relative",
    background: "#ffffff",
    borderRadius: "8px",
    padding: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  qrCode: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: "4px",
  },
  qrCodePlaceholder: {
    display: "none",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    color: "#6b7280",
  },
  placeholderIcon: {
    fontSize: "48px",
    marginBottom: "8px",
  },
  placeholderText: {
    fontSize: "14px",
  },
  shopLink: {
    fontSize: "14px",
    color: "#3b82f6",
    fontWeight: "500",
    marginTop: "auto",
  },
  communitiesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "24px",
    marginTop: "30px",
  },
  communityCard: {
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: "16px",
    padding: "24px",
    textDecoration: "none",
    color: "inherit",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    transition: "all 0.3s ease",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
  },
  communityTitleImage: {
    width: "100%",
    maxWidth: "200px",
    height: "auto",
    marginBottom: "16px",
    borderRadius: "8px",
    objectFit: "contain",
  },
  communityIcon: {
    fontSize: "48px",
    marginBottom: "12px",
  },
  communityName: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#ffffff",
    marginBottom: "16px",
  },
  contactBox: {
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: "12px",
    padding: "40px",
    marginTop: "30px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    maxWidth: "600px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    textAlign: "left",
  },
  contactIcon: {
    fontSize: "32px",
    flexShrink: 0,
  },
  contactInfo: {
    flex: 1,
  },
  contactLabel: {
    fontSize: "14px",
    color: "#9ca3af",
    marginBottom: "8px",
    fontWeight: "500",
  },
  contactValue: {
    fontSize: "18px",
    color: "#3b82f6",
    textDecoration: "none",
    fontWeight: "500",
    transition: "color 0.2s",
  },
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/manuals/:id" element={<ManualViewerPage />} />
          <Route path="/" element={<Layout />}>
            {/* 主站完整功能（备案完成后还原） */}
            <Route index element={<Home />} />
            <Route path="Home" element={<Home />} />
            <Route path="login" element={<Login />} />
            <Route path="about" element={<About />} />
            <Route path="products" element={<Products />} />
            <Route path="products/:id" element={<ProductDetail />} />
            <Route path="profile" element={<Profile />} />
            <Route path="forum" element={<Forum />} />
            <Route path="studio-videos" element={<StudioVideos />} />
            <Route path="tutorials" element={<Tutorials />} />
            <Route path="settings" element={<Settings />} />
            <Route path="studio" element={<StudioEmbed />} />

            {/* 旧路径 /real/* 重定向到主站 */}
            <Route path="real/*" element={<RealLegacyRedirect />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
